import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('data-disabled')) return;

    const [items] = this.refs.marqueeItems;
    if (!items) return;

    this.#items = items;
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(items);

    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #items = null;

  #resizeObserver = new ResizeObserver(() => this.#update());

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #update() {
    const { content } = this.refs;
    const items = this.#items;
    if (!items) return;

    const marqueeWidth = this.clientWidth;
    const itemsWidth = items.getBoundingClientRect().width;

    if (marqueeWidth === 0 || itemsWidth === 0) return;

    const numberOfCopies = Math.ceil(marqueeWidth / itemsWidth);

    if (numberOfCopies !== content.children.length || !this.clonedContent) {
      this.#setNumberOfCopies(numberOfCopies);
      this.#duplicateContent();
      this.#setSpeed(this.#calculateSpeed(numberOfCopies));
    }

    this.#setOffset();
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #setOffset() {
    const clone = this.clonedContent;
    if (!clone) return;

    const offset = this.refs.content.getBoundingClientRect().left - clone.getBoundingClientRect().left;
    this.style.setProperty('--marquee-offset', `${offset}px`);
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    removeRefs(clone);

    this.refs.wrapper.appendChild(clone);
  }

  #setNumberOfCopies(numberOfCopies) {
    const { content } = this.refs;
    const items = this.#items;
    if (!items) return;

    while (content.children.length > Math.max(numberOfCopies, 1)) {
      content.lastElementChild?.remove();
    }

    while (content.children.length < numberOfCopies) {
      const clone = /** @type {HTMLElement} */ (items.cloneNode(true));

      clone.setAttribute('aria-hidden', 'true');
      removeRefs(clone);

      content.appendChild(clone);
    }
  }
}

function removeRefs(element) {
  element.removeAttribute('ref');

  for (const child of element.querySelectorAll('[ref]')) {
    child.removeAttribute('ref');
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
